import { useEffect, RefObject } from 'react'
import { User } from '@weblite/ts-action/user'
import { useStateRef } from '@weblite/logic-toolkit'
import { nothing } from '$func/utils'
import {
  resetEditor,
  insertContent,
  convertHtmlToFormattedText,
  insertMentionIntoEditor,
  putCaretAtEndOfEditor,
} from './ops'

interface EditorAPI {
  _getApiValues: (editorId?: string) => EditorAPI | undefined
  insertText: (text: string, editorId?: string) => void
  insertMention: (user: User, editorId?: string) => void

  focus: (editorId?: string) => void
  blur: (editorId?: string) => void
  setText: (formattedText: string, editorId?: string) => void
  getText: (editorId?: string) => string

  setKeepFocus: (value: boolean, editorId?: string) => void
  setHasFocus: (value: boolean, editorId?: string) => void

  getKeepFocus: (editorId?: string) => boolean
  getHasFocus: (editorId?: string) => boolean
}

const apiValuesMap = new Map<string, EditorAPI>()

let apiValues: EditorAPI = {
  _getApiValues: () => undefined,

  insertText: nothing,
  insertMention: nothing,

  focus: nothing,
  blur: nothing,
  setText: nothing,
  getText: () => '',

  setKeepFocus: nothing,
  setHasFocus: nothing,
  getKeepFocus: () => false,
  getHasFocus: () => false,
}

export const editorAPI: EditorAPI = {
  _getApiValues: (editorId?: string) => {
    if (editorId) return apiValuesMap.get(editorId)
    return apiValues
  },
  insertText: (text, editorId) => {
    // eslint-disable-next-line @typescript-eslint/dot-notation
    editorAPI['_getApiValues'](editorId)?.insertText(text)
  },
  insertMention: (user, editorId) =>
    // eslint-disable-next-line @typescript-eslint/dot-notation
    editorAPI['_getApiValues'](editorId)?.insertMention(user),
  // eslint-disable-next-line @typescript-eslint/dot-notation
  focus: editorId => editorAPI['_getApiValues'](editorId)?.focus(),
  // eslint-disable-next-line @typescript-eslint/dot-notation
  blur: editorId => editorAPI['_getApiValues'](editorId)?.blur(),
  setText: (text, editorId) =>
    // eslint-disable-next-line @typescript-eslint/dot-notation
    editorAPI['_getApiValues'](editorId)?.setText(text),
  // eslint-disable-next-line @typescript-eslint/dot-notation
  getText: editorId => String(editorAPI['_getApiValues'](editorId)?.getText()),

  setKeepFocus: (value: boolean, editorId) =>
    // eslint-disable-next-line @typescript-eslint/dot-notation
    editorAPI['_getApiValues'](editorId)?.setKeepFocus(value),

  setHasFocus: (value: boolean, editorId) =>
    // eslint-disable-next-line @typescript-eslint/dot-notation
    editorAPI['_getApiValues'](editorId)?.setHasFocus(value),

  getHasFocus: (editorId?: string) => {
    // eslint-disable-next-line @typescript-eslint/dot-notation
    return Boolean(editorAPI['_getApiValues'](editorId)?.hasFocus)
  },

  getKeepFocus: (editorId?: string) => {
    // eslint-disable-next-line @typescript-eslint/dot-notation
    return Boolean(editorAPI['_getApiValues'](editorId)?.keepFocus)
  },
}

function initiateEditorAPI(api: Partial<EditorAPI>, editorId?: string) {
  if (editorId) apiValuesMap.set(editorId, { ...apiValues, ...api })
  else {
    apiValues = { ...apiValues, ...api }
    // @ts-ignore
    window.editor = apiValues
  }
}

export function useEditorAPI(
  EditorRef: RefObject<HTMLDivElement>,
  updateEditorState: (contentChanged?: boolean) => void,
  editorId?: string,
) {
  const updateEditorStateRef = useStateRef(updateEditorState)
  useEffect(() => {
    if (!EditorRef.current) return
    EditorRef.current.addEventListener('paste', () => {
      EditorRef.current?.blur()
      EditorRef.current?.focus()
    })
  }, [EditorRef])

  useEffect(() => {
    initiateEditorAPI(
      {
        insertText(text: string) {
          const editor = EditorRef.current
          if (!editor) return
          insertContent(editor, text)
          updateEditorStateRef.current()

          editor.focus()
        },

        insertMention(user: User) {
          const editor = EditorRef.current
          if (!editor) return

          insertMentionIntoEditor(editor, user)
          updateEditorStateRef.current()
        },

        focus() {
          const editor = EditorRef.current
          if (!editor) return
          editor.focus()
        },

        blur() {
          const editor = EditorRef.current
          if (!editor) return
          editor.blur()
        },

        setText(text: string) {
          const editor = EditorRef.current
          if (!editor) return
          putCaretAtEndOfEditor(editor)
          resetEditor(editor)
          insertContent(editor, text)
          editor.scrollTo?.(0, editor.scrollHeight)
          editor.focus()
          updateEditorStateRef.current()
        },

        getText() {
          const editor = EditorRef.current
          if (!editor) return ''
          return convertHtmlToFormattedText(editor) || ''
        },
      },
      editorId,
    )
  }, [updateEditorStateRef, EditorRef, editorId])
}
